<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { computed, onUnmounted, ref } from "vue";
import { type Board, createBoard, findWinningLine, isBoardFull, pickKioskMove } from "./ticTacToe";

const { t } = useTranslation();

const KIOSK_DELAY = 520;
const RESTART_DELAY = 1800;

const board = ref<Board>(createBoard());
const isLocked = ref(false);
const round = ref(0);
const score = ref({ player: 0, kiosk: 0, draw: 0 });

let kioskTimer: ReturnType<typeof setTimeout> | null = null;
let restartTimer: ReturnType<typeof setTimeout> | null = null;

const winningLine = computed(() => findWinningLine(board.value));
const winner = computed(() => (winningLine.value ? board.value[winningLine.value[0] as number] : null));
const isDraw = computed(() => !winner.value && isBoardFull(board.value));

const statusText = computed(() => {
	if (winner.value === "x") return t(`kso.print.game.win`);
	if (winner.value === "o") return t(`kso.print.game.lose`);
	if (isDraw.value) return t(`kso.print.game.draw`);
	return t(`kso.print.game.turn`);
});

const clearTimers = () => {
	if (kioskTimer) clearTimeout(kioskTimer);
	if (restartTimer) clearTimeout(restartTimer);
	kioskTimer = null;
	restartTimer = null;
};

const restart = () => {
	board.value = createBoard();
	isLocked.value = false;
	round.value += 1;
};

const finishRound = () => {
	if (winner.value === "x") score.value.player += 1;
	else if (winner.value === "o") score.value.kiosk += 1;
	else score.value.draw += 1;

	isLocked.value = true;
	restartTimer = setTimeout(restart, RESTART_DELAY);
};

const kioskMove = () => {
	const index = pickKioskMove(board.value, round.value + score.value.player + score.value.kiosk);
	if (index !== null) board.value[index] = "o";

	if (winningLine.value || isDraw.value) {
		finishRound();
		return;
	}

	isLocked.value = false;
};

const playerMove = (index: number) => {
	if (isLocked.value || board.value[index]) return;

	board.value[index] = "x";
	isLocked.value = true;

	if (winningLine.value || isDraw.value) {
		finishRound();
		return;
	}

	kioskTimer = setTimeout(kioskMove, KIOSK_DELAY);
};

const isWinningCell = (index: number) => !!winningLine.value?.includes(index);

onUnmounted(clearTimers);
</script>

<template>
	<div class="kso_game">
		<div class="kso_game_head">
			<p class="kso_game_score">{{ t(`kso.print.game.score`, { player: score.player, kiosk: score.kiosk }) }}</p>
			<p class="kso_game_status">{{ statusText }}</p>
		</div>

		<div class="kso_game_board" :key="round">
			<svg class="kso_game_grid" viewBox="0 0 300 300" aria-hidden="true">
				<path d="M102,12 C99,90 105,215 100,289" />
				<path d="M199,10 C203,95 196,208 201,290" />
				<path d="M11,101 C95,97 210,105 290,99" />
				<path d="M10,200 C90,205 208,195 288,201" />
			</svg>

			<button
				v-for="(cell, index) in board"
				:key="index"
				type="button"
				class="kso_game_cell"
				:data-win="isWinningCell(index)"
				:disabled="isLocked || !!cell"
				@click="playerMove(index)"
			>
				<svg v-if="cell === 'x'" class="kso_game_mark kso_game_mark_x" viewBox="0 0 100 100" aria-hidden="true">
					<path d="M22,20 C42,42 58,60 79,81" />
					<path d="M80,21 C58,42 41,59 21,80" />
				</svg>
				<svg v-else-if="cell === 'o'" class="kso_game_mark kso_game_mark_o" viewBox="0 0 100 100" aria-hidden="true">
					<path d="M50,17 C74,17 85,34 85,51 C85,71 71,84 50,84 C30,84 16,70 16,50 C16,32 29,18 52,17" />
				</svg>
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './ksoTicTacToe.scss';
</style>
